// ===========================================
// Tag Filter Component - Smart Tag Drill-Down
// ===========================================
// Displays tag cloud with counts for filtering prompts
// Supports multi-select, search autocomplete, and tag categories
// Auto-tags detected: mongodb, react, typescript, etc.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Hash, 
  X, 
  Search, 
  ChevronDown, 
  Check,
  Sparkles,
  Filter
} from 'lucide-react';
import { promptsAPI } from '../services/api';
import './TagFilter.scss';

const TagFilter = ({ 
  selectedTags = [], 
  onTagsChange,
  compact = false 
}) => {
  // ===========================================
  // State
  // ===========================================
  const [allTags, setAllTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // ===========================================
  // Fetch Tag Statistics
  // ===========================================
  // Gets all tags with counts for the tag cloud
  const fetchTagStats = useCallback(async () => {
    try {
      const response = await promptsAPI.getTagStats();
      if (response.data.success) {
        setAllTags(response.data.data.tags || []);
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
    setLoading(false);
  }, []);

  // Fetch tags on mount
  useEffect(() => {
    fetchTagStats();
  }, [fetchTagStats]);

  // ===========================================
  // Tag Search Autocomplete
  // ===========================================
  // Debounced search for tag suggestions
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const searchTags = async () => {
      try {
        const response = await promptsAPI.searchTags(searchQuery);
        if (response.data.success) {
          setSearchResults(response.data.data.tags || []);
        }
      } catch (err) {
        console.error('Tag search error:', err);
      }
    };

    // Debounce search by 200ms
    const timer = setTimeout(searchTags, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ===========================================
  // Click Outside Handler
  // ===========================================
  // Closes dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showDropdown]);

  // ===========================================
  // Handlers
  // ===========================================
  
  // Toggle tag selection (add/remove from filter)
  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  // Clear all selected tags
  const clearAllTags = () => {
    onTagsChange([]);
  };

  // Handle dropdown toggle
  const handleToggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      setSearchQuery(''); // Clear search when opening
    }
  };

  // ===========================================
  // Render Helpers
  // ===========================================
  
  // Get display tags (search results or top tags)
  const displayTags = searchQuery.length >= 2 ? searchResults : allTags.slice(0, 30);
  
  // Calculate tag size based on count for visual weight
  const getTagSize = (count) => {
    const maxCount = Math.max(...allTags.map(t => t.count), 1);
    const ratio = count / maxCount;
    if (ratio > 0.7) return 'large';
    if (ratio > 0.3) return 'medium';
    return 'small';
  };

  // ===========================================
  // Render
  // ===========================================
  return (
    <div className={`tag-filter ${compact ? 'tag-filter--compact' : ''}`} ref={dropdownRef}>
      {/* Selected Tags Display - Shows active filters */}
      {selectedTags.length > 0 && (
        <div className="tag-filter__selected">
          <span className="tag-filter__selected-label">
            <Filter size={12} />
            Filtering by:
          </span>
          <div className="tag-filter__selected-tags">
            {selectedTags.map(tag => (
              <span key={tag} className="tag-filter__chip tag-filter__chip--active">
                <Hash size={10} />
                {tag}
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTag(tag);
                  }}
                  aria-label={`Remove ${tag} filter`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          {selectedTags.length > 1 && (
            <button 
              className="tag-filter__clear" 
              onClick={clearAllTags}
              title="Clear all tag filters"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Tag Filter Dropdown Trigger */}
      <div className="tag-filter__dropdown-wrapper">
        <button 
          className={`tag-filter__trigger ${showDropdown ? 'active' : ''}`}
          onClick={handleToggleDropdown}
        >
          <Hash size={14} />
          <span>Filter by Tag</span>
          <span className="tag-filter__badge">
            <Sparkles size={10} />
            Auto
          </span>
          <ChevronDown size={14} className={`tag-filter__chevron ${showDropdown ? 'rotated' : ''}`} />
        </button>

        {/* Dropdown Panel */}
        {showDropdown && (
          <div className="tag-filter__dropdown">
            {/* Search Input */}
            <div className="tag-filter__search">
              <Search size={14} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search tags... (e.g., mongodb, react)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button 
                  className="tag-filter__search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {/* Tag List / Cloud */}
            <div className="tag-filter__list">
              {loading ? (
                <div className="tag-filter__loading">
                  <div className="tag-filter__spinner" />
                  Loading tags...
                </div>
              ) : displayTags.length === 0 ? (
                <div className="tag-filter__empty">
                  {searchQuery ? (
                    <>
                      <Hash size={24} />
                      <p>No tags matching "{searchQuery}"</p>
                    </>
                  ) : (
                    <>
                      <Sparkles size={24} />
                      <p>No tags yet</p>
                      <span>Tags are auto-detected when you create prompts</span>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {/* Tag count summary */}
                  <div className="tag-filter__summary">
                    {searchQuery 
                      ? `${displayTags.length} matching tags`
                      : `${allTags.length} technology tags detected`
                    }
                  </div>
                  
                  {/* Tag cloud/list */}
                  <div className="tag-filter__tags">
                    {displayTags.map(({ tag, count }) => {
                      const isSelected = selectedTags.includes(tag);
                      const size = getTagSize(count);
                      
                      return (
                        <button
                          key={tag}
                          className={`tag-filter__item tag-filter__item--${size} ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleTag(tag)}
                          title={`${count} prompt${count !== 1 ? 's' : ''} with ${tag}`}
                        >
                          <Hash size={11} />
                          <span className="tag-filter__item-name">{tag}</span>
                          <span className="tag-filter__item-count">{count}</span>
                          {isSelected && <Check size={12} className="tag-filter__item-check" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer hint */}
            <div className="tag-filter__footer">
              <Sparkles size={10} />
              Tags auto-detected from prompt content
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TagFilter;

